import { useEffect, useRef, useCallback } from "react";
import { EditorScene } from "../canvas/EditorScene.js";
import {
  createCamera,
  applyCamera,
  screenToWorld,
  zoomAt,
  panBy,
  type CameraState,
} from "../canvas/camera.js";
import { SnapIndex } from "../canvas/snapping.js";
import { useEditor } from "../store/editor.js";
import type { Vec2 } from "@maps/schema";

const ROAD_DEFAULTS = { width: 8, roadType: "street" as const };
const BUILDING_DEFAULTS = { height: 10, style: "flat" as const };

interface DrawingState {
  vertices: Vec2[];
  snapTarget: Vec2 | null;
}

export function CanvasView(): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<EditorScene | null>(null);
  const camRef = useRef<CameraState>(createCamera());
  const snapRef = useRef(new SnapIndex());

  // Drag state
  const panningRef = useRef(false);
  const lastPanRef = useRef({ x: 0, y: 0 });
  const spaceDownRef = useRef(false);

  // Drawing state (per tool session)
  const drawingRef = useRef<DrawingState>({ vertices: [], snapTarget: null });

  const editor = useEditor();
  const { doc, tool, selectedId, setTool, select, addRoad, addBuilding, deleteFeature } = editor;

  // Initialize Pixi
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const scene = new EditorScene();
    sceneRef.current = scene;
    void scene.init(canvas).then(() => {
      rerender();
      scene.app.ticker.add(() => {
        // Could do per-frame updates here if needed
      });
    });

    return () => {
      scene.destroy();
      sceneRef.current = null;
    };
  }, []);

  // Rebuild snap index when doc changes
  useEffect(() => {
    snapRef.current.rebuild(doc);
  }, [doc]);

  // Rerender when doc/selection/camera changes
  const rerender = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene || !scene.ready) return;
    const cam = camRef.current;
    applyCamera(scene.worldLayer, cam, scene.canvasWidth, scene.canvasHeight);
    scene.renderGrid(cam);
    scene.renderDoc(doc, selectedId);
    // Keep overlay if drawing
    const drawing = drawingRef.current;
    if (drawing.vertices.length > 0 || drawing.snapTarget) {
      const cursor = drawing.snapTarget;
      scene.renderOverlay(drawing.vertices, cursor, drawing.snapTarget);
    } else {
      scene.clearOverlay();
    }
  }, [doc, selectedId]);

  useEffect(() => {
    rerender();
  }, [rerender]);

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;

      if (e.code === "Space") {
        spaceDownRef.current = true;
        return;
      }
      if (e.key === "Escape") {
        drawingRef.current = { vertices: [], snapTarget: null };
        setTool("select");
        rerender();
      }
      if (e.key === "Enter") {
        finishDrawing();
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedId) deleteFeature(selectedId);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        if (e.shiftKey) editor.redo();
        else editor.undo();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === "Space") spaceDownRef.current = false;
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [selectedId, setTool, deleteFeature, rerender, editor]);

  const getWorldPos = (e: React.PointerEvent): Vec2 => {
    const scene = sceneRef.current!;
    const rect = scene.app.canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const cam = camRef.current;
    const w = screenToWorld(sx, sy, cam, scene.canvasWidth, scene.canvasHeight);
    return { x: w.x, y: w.y };
  };

  const snapToWorld = (world: Vec2): Vec2 => {
    const snap = snapRef.current.findNearest(world.x, world.y, camRef.current.zoom);
    // Also snap to first vertex (close polygon)
    const drawing = drawingRef.current;
    if (drawing.vertices.length >= 3) {
      const first = drawing.vertices[0]!;
      const distToFirst = Math.hypot(first.x - world.x, first.y - world.y);
      const snapDist = 12 / camRef.current.zoom;
      if (distToFirst < snapDist) return { x: first.x, y: first.y };
    }
    return snap ?? world;
  };

  const finishDrawing = () => {
    const drawing = drawingRef.current;
    if (drawing.vertices.length < 2) {
      drawingRef.current = { vertices: [], snapTarget: null };
      rerender();
      return;
    }

    if (tool === "road") {
      addRoad({
        centerline: drawing.vertices,
        width: ROAD_DEFAULTS.width,
        roadType: ROAD_DEFAULTS.roadType,
      });
    } else if (tool === "building") {
      if (drawing.vertices.length >= 3) {
        addBuilding({
          polygon: drawing.vertices,
          height: BUILDING_DEFAULTS.height,
          style: BUILDING_DEFAULTS.style,
        });
      }
    }

    drawingRef.current = { vertices: [], snapTarget: null };
    rerender();
  };

  const onPointerDown = (e: React.PointerEvent) => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Space-drag pan or middle-click pan
    if (spaceDownRef.current || e.button === 1) {
      panningRef.current = true;
      lastPanRef.current = { x: e.clientX, y: e.clientY };
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }

    if (tool === "select") {
      // Click on feature to select (simple hit-test via world pos)
      const world = getWorldPos(e);
      const hit = hitTest(doc, world.x, world.y);
      select(hit);
      return;
    }

    if (tool === "road" || tool === "building") {
      const world = getWorldPos(e);
      const snapped = snapToWorld(world);
      const drawing = drawingRef.current;

      // Double-click or click near first vertex = finish
      if (tool === "building" && drawing.vertices.length >= 3) {
        const first = drawing.vertices[0]!;
        if (Math.hypot(first.x - snapped.x, first.y - snapped.y) < 0.5) {
          finishDrawing();
          return;
        }
      }

      drawing.vertices.push({ x: snapped.x, y: snapped.y });
      drawing.snapTarget = null;
      rerender();
      return;
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (panningRef.current) {
      const dx = e.clientX - lastPanRef.current.x;
      const dy = e.clientY - lastPanRef.current.y;
      lastPanRef.current = { x: e.clientX, y: e.clientY };
      camRef.current = panBy(camRef.current, dx, dy);
      rerender();
      return;
    }

    if (tool === "road" || tool === "building") {
      const world = getWorldPos(e);
      const snapped = snapToWorld(world);
      drawingRef.current.snapTarget = snapped;
      rerender();
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (panningRef.current) {
      panningRef.current = false;
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const onDoubleClick = (_e: React.MouseEvent) => {
    if (tool === "road" || tool === "building") {
      finishDrawing();
    }
  };

  const onWheel = (e: React.WheelEvent) => {
    const scene = sceneRef.current;
    if (!scene) return;
    const rect = scene.app.canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    camRef.current = zoomAt(
      camRef.current,
      sx,
      sy,
      scene.canvasWidth,
      scene.canvasHeight,
      factor,
    );
    rerender();
  };

  return (
    <canvas
      ref={canvasRef}
      style={{ width: "100%", height: "100%", display: "block", touchAction: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onDoubleClick={onDoubleClick}
      onWheel={onWheel}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}

/** Simple hit-test: find the topmost feature near (wx, wy). */
function hitTest(doc: { features: { id: string; type: string }[] }, wx: number, wy: number): string | null {
  // Iterate in reverse (topmost first). For now, a simple distance check.
  for (let i = doc.features.length - 1; i >= 0; i--) {
    const f = doc.features[i]!;
    if (f.type === "road") {
      const road = f as unknown as { id: string; centerline: Vec2[]; width: number };
      if (nearPolyline(wx, wy, road.centerline, road.width / 2 + 2)) return road.id;
    } else if (f.type === "building") {
      const b = f as unknown as { id: string; polygon: Vec2[] };
      if (pointInPolygon(wx, wy, b.polygon)) return b.id;
    }
  }
  return null;
}

function nearPolyline(px: number, py: number, pts: Vec2[], maxDist: number): boolean {
  for (let i = 0; i < pts.length - 1; i++) {
    if (distToSegment(px, py, pts[i]!, pts[i + 1]!) < maxDist) return true;
  }
  return false;
}

function distToSegment(px: number, py: number, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx === 0 && dy === 0) return Math.hypot(px - a.x, py - a.y);
  const t = Math.max(0, Math.min(1, ((px - a.x) * dx + (py - a.y) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(px - (a.x + t * dx), py - (a.y + t * dy));
}

function pointInPolygon(px: number, py: number, poly: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i]!.x, yi = poly[i]!.y;
    const xj = poly[j]!.x, yj = poly[j]!.y;
    const intersect = ((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}